// Pure class-composition seam for action surfaces. Two consumers:
//
//   1. <Button> — the wrapper component, when the element is <button>.
//   2. Any non-button element that should LOOK like a button — typically a
//      React Router <Link> — calls `buttonClasses()` directly with the same
//      variant/size vocabulary.
//
// Kept in its own (non-React) module so the React-refresh "only export
// components" rule stays clean on Button.tsx, and so the function can be
// unit-tested without React in scope. The interface here IS the test surface.

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "toggle"
  | "chip"
  | "ghost"
  | "icon";

// Control-height scale:
//   sm      — chip-sized, 24px tall. Inline-dense action (chat clear, inline
//             toolbar actions). text-[10px], px-3 py-1.
//   compact — 32px tall (--h-control-sm). Card-row CTAs (Save list rows,
//             SharePage landing actions, PowerDetail "Start" CTA). text-[11px],
//             px-4 py-2.
//   md      — 40px tall (--h-control-md). The default. Form rhythm, primary
//             page actions. text-[11px], px-5 py-2.5.
//   lg      — 48px tall (--h-control-lg). Hero-only.
//   handle  — 48px square circular control. Slider/threshold handles only.
export type ButtonSize = "sm" | "compact" | "md" | "lg" | "handle";

export interface ButtonClassesOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "smycles-button",
  secondary: "smycles-dismiss-button",
  destructive: "smycles-danger-button",
  toggle: "smycles-toggle-button",
  chip: "smycles-chip-button",
  ghost: "text-ink/55 hover:text-ink",
  icon: "text-ink/55 hover:bg-ink/[0.06] hover:text-ink",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "px-3 py-1 text-[10px]",
  compact: "px-4 py-2 text-[11px]",
  md: "px-5 py-2.5 text-[11px]",
  lg: "px-6 py-3 text-xs",
  handle: "h-12 w-12 p-0 text-[12px] shadow-xl",
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: "h-8 w-8 text-xs",
  compact: "h-9 w-9 text-xs",
  md: "h-9 w-9 text-sm",
  lg: "h-12 w-12 text-base",
  handle: "h-12 w-12 text-[12px] shadow-xl",
};

const BASE =
  "inline-flex items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:pointer-events-none disabled:opacity-50";

export function buttonClasses({
  variant = "primary",
  size = "md",
  className = "",
}: ButtonClassesOptions = {}): string {
  const sizeClass = variant === "icon" ? ICON_SIZES[size] : SIZES[size];
  return [BASE, VARIANTS[variant], sizeClass, className]
    .filter(Boolean)
    .join(" ");
}
